"use client";

/**
 * ThemeToggle — light/dark/auto switch. Persists to localStorage.
 * No external dependency (next-themes alternative).
 */
import * as React from "react";

type Theme = "light" | "dark" | "auto";

export function ThemeToggle(): React.JSX.Element {
  const [theme, setTheme] = React.useState<Theme>("auto");

  React.useEffect(() => {
    const saved = (localStorage.getItem("commerco-theme") as Theme) ?? "auto";
    setTheme(saved);
    applyTheme(saved);
  }, []);

  const applyTheme = (t: Theme) => {
    const root = document.documentElement;
    if (t === "auto") {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      root.dataset.theme = prefersDark ? "dark" : "light";
      root.classList.toggle("dark", prefersDark);
    } else {
      root.dataset.theme = t;
      root.classList.toggle("dark", t === "dark");
    }
  };

  const cycle = () => {
    const next: Theme = theme === "auto" ? "light" : theme === "light" ? "dark" : "auto";
    setTheme(next);
    localStorage.setItem("commerco-theme", next);
    applyTheme(next);
  };

  // Listen to OS preference changes when in auto mode
  React.useEffect(() => {
    if (theme !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("auto");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const icon = theme === "dark" ? "🌙" : theme === "light" ? "☀️" : "🌗";
  const label = theme === "dark" ? "Koyu" : theme === "light" ? "Açık" : "Otomatik";

  return (
    <button
      type="button"
      onClick={cycle}
      className="flex h-8 items-center gap-1 rounded-md px-2 text-xs text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-default)]"
      aria-label={`Tema: ${label} (değiştirmek için tıkla)`}
      title={`Tema: ${label}`}
    >
      <span aria-hidden>{icon}</span>
      <span className="hidden md:inline">{label}</span>
    </button>
  );
}
